'use client';

import { LinePrice } from '@/components/checkout/LinePrice';
import { TotalAmount } from '@/components/checkout/TotalAmount';
import { Icon } from '@/components/ui/Icon';
import { InlineErrorAlert } from '@/components/ui/InlineErrorAlert';
import { TaxLine } from '@/components/ui/Tax';
import { useCheckout } from '@/hooks/useCheckout';
import type { ShoppingBagLineErrors } from '@/lib/checkout/errors/parseShoppingBagLineErrors';
import type { CheckoutPreOrderSummary } from '@/lib/checkout/preOrderSummary';
import { isGiftProductType } from '@/lib/gwp';
import { formatPrice } from '@/lib/utils/formatPrice';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { GiftLineName } from '../GiftLineName';
import type { Ref } from 'react';
import { PreOrderFooterBreakdown } from '../PreOrderFooterBreakdown';
import { ShippingInfoBox } from '../ShippingInfoBox/ShippingInfoBox';
import styles from './OrderDetails.module.scss';

export interface OrderDetailsLine {
  id: string;
  name: string;
  sku?: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  image?: string;
  /** Saleor product ID — used to fetch translated GWP name via the details endpoint. */
  productId?: string;
  /** Product type slug from Saleor (e.g. 'gift'). Used to show "Complimentary" instead of price. */
  productTypeSlug?: string | null;
  /** Product type display name from Saleor (e.g. 'Watch'). Used for watch detection parity with checkout. */
  productTypeName?: string | null;
}

export interface OrderDetailsPricing {
  subtotal: number;
  shipping: number;
  tax: number;
  discount: number;
  total: number;
  currency: string;
  isTaxCalculating?: boolean;
  hasAddressForTax?: boolean;
  retailDeliveryFee?: number;
  isColorado?: boolean;
  preOrder?: CheckoutPreOrderSummary;
}

export interface OrderDetailsData {
  lines: OrderDetailsLine[];
  pricing: OrderDetailsPricing;
}

export interface OrderDetailsProps {
  data: OrderDetailsData;
  showItems?: boolean;
  compact?: boolean;
  channelSlug?: string;
  isDepositPaid?: boolean;
  /**
   * Label remaining amount as Final payment (Breitling parity).
   * Used after balance/final payment on confirmation, and on pay-per-link balance charge.
   */
  useFinalPaymentLabel?: boolean;
  /** Per-SKU OrderApp errors (CPI mismatch, stock, pre-order eligibility). */
  lineErrors?: ShoppingBagLineErrors;
  /** Attached to the first line-level error alert (parent scroll-to-error). */
  firstLineErrorRef?: Ref<HTMLDivElement>;
}

export function OrderDetails({
  data,
  showItems = true,
  compact = false,
  channelSlug: channelSlugProp,
  isDepositPaid = false,
  useFinalPaymentLabel = false,
  lineErrors,
  firstLineErrorRef,
}: Readonly<OrderDetailsProps>) {
  const t = useTranslations('checkout');
  const { channelSlug: checkoutChannelSlug } = useCheckout();
  const channelSlug = channelSlugProp ?? checkoutChannelSlug;
  const params = useParams();
  const locale = (params?.lang as string) || 'us-en';

  const { lines, pricing } = data;
  const countryCode = locale.split('-')[0].toUpperCase();

  const formatAmount = (amount: number | undefined, curr: string) => {
    if (amount === undefined || amount === null) return '-';
    return formatPrice(amount, curr, locale);
  };

  const isFreeShipping = pricing.shipping === 0;

  const displayLines = lines.flatMap((line) =>
    Array.from({ length: line.quantity }, (_, i) => ({
      key: `${line.id}-${i}`,
      name: line.name,
      sku: line.sku,
      unitPrice: line.unitPrice,
      currency: line.currency,
      image: line.image,
      isGift: isGiftProductType(line.productTypeSlug) || isGiftProductType(line.productTypeName),
      productId: line.productId,
    }))
  );

  const firstErrorIndex = displayLines.findIndex((candidate) =>
    Boolean(candidate.sku && lineErrors?.[candidate.sku]?.message)
  );

  return (
    <div className={styles.wrapper}>
      {showItems && (
        <div className={styles.items}>
          {displayLines.map((line, index) => {
            const lineErrorMessage =
              line.sku && lineErrors ? lineErrors[line.sku]?.message : undefined;

            return (
              <div key={line.key} className={styles.item}>
                <div className={styles.itemImage}>
                  {line.image ? (
                    <Image
                      src={line.image}
                      alt={line.name}
                      fill
                      sizes="60px"
                      className={styles.image}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder}>
                      <Icon name="watch" size={20} />
                    </div>
                  )}
                </div>
                <div className={styles.itemInfo}>
                  <span className={styles.itemName}>
                    {line.isGift ? (
                      <GiftLineName
                        productId={line.productId}
                        channelSlug={channelSlug}
                        locale={locale}
                        fallbackName={line.name}
                      />
                    ) : (
                      line.name
                    )}
                  </span>
                  {line.sku && (
                    <span className={styles.itemSku}>
                      {t('orderSummary.ref')} {line.sku}
                    </span>
                  )}
                </div>
                <LinePrice
                  price={line.unitPrice}
                  currency={line.currency}
                  countryCode={countryCode}
                  isGift={line.isGift}
                />
                {lineErrorMessage ? (
                  <div ref={index === firstErrorIndex ? firstLineErrorRef : undefined}>
                    <InlineErrorAlert
                      className={styles.lineError}
                      data-testid="shopping-bag-line-error"
                    >
                      {lineErrorMessage}
                    </InlineErrorAlert>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.totals}>
        <div className={styles.row}>
          <span>{t('cart.subtotal')}</span>
          <span className={styles.amount}>{formatAmount(pricing.subtotal, pricing.currency)}</span>
        </div>

        {pricing.discount > 0 && (
          <div className={clsx(styles.row, styles.discount)}>
            <span>{t('orderSummary.discount')}</span>
            <span className={styles.amount}>
              -{formatAmount(pricing.discount, pricing.currency)}
            </span>
          </div>
        )}

        <div className={styles.row}>
          <span>{t('cart.shipping')}</span>
          <span className={clsx(styles.amount, isFreeShipping && styles.free)}>
            {isFreeShipping
              ? t('cart.freeLabel')
              : formatAmount(pricing.shipping, pricing.currency)}
          </span>
        </div>

        {!compact && (
          <>
            <div className={styles.row}>
              <TaxLine
                countryCode={countryCode}
                amount={pricing.tax}
                currency={pricing.currency}
                context="checkout"
                locale={locale}
                loading={pricing.isTaxCalculating}
                loadingElement={
                  <span className={styles.calculated}>
                    {t('orderSummary.calculatedAtCheckout')}
                  </span>
                }
              />
            </div>

            {pricing.isColorado && (pricing.retailDeliveryFee ?? 0) > 0 && (
              <div className={styles.row}>
                <span>{t('orderSummary.retailDeliveryFees')}</span>
                <span className={styles.amount}>
                  {formatAmount(pricing.retailDeliveryFee, pricing.currency)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <TotalAmount
        amount={formatAmount(pricing.total, pricing.currency)}
        countryCode={countryCode}
        context="order"
        hasShippingAddress={pricing.hasAddressForTax}
      />

      {pricing.preOrder && (
        <PreOrderFooterBreakdown
          summary={pricing.preOrder}
          isDepositPaid={isDepositPaid}
          useFinalPaymentLabel={useFinalPaymentLabel}
          showSummaryNote
          className={styles.preOrderBreakdown}
        />
      )}

      <ShippingInfoBox channelSlug={channelSlug ?? undefined} />
    </div>
  );
}
